import {
  ColumnDetail,
  ComplexSelectionFormValue,
  RowDetail,
} from '../../../../../../../ui/gzSelectableTableRowGroup/types';
import {
  AntimalwareAntiExploitSystemWideDetectionsWindows,
  AntimalwareAntiExploitSystemWideDetectionsWindowsAction,
  AntimalwareAntiExploitSystemWideDetectionsLinux,
  AntimalwareAntiExploitSystemWideDetectionsLinuxAction,
} from '@nirvana/shared/dtos/types/policies/common';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { AntimalwareAntiExploitSearchableKeywords } from '../../../../utils/uiSettings/antimalware/antimalware.types';
import { SearchableKeyword } from '../../../../../../../ui/gzSearchableAccordionsPanel/types';

/* System wide detections - Windows region */
export enum AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey {
  DPI_DETECTIONS = 'dpiDetections',
  PRIVILEGE_ESCALATION = 'privilegeEscalation',
  LSASS_PROTECTION = 'lsassMemoryAccessFromUnknownProcess',
}

export const systemWideDetectionsWindowsColumns =
  (): ColumnDetail<AntimalwareAntiExploitSystemWideDetectionsWindowsAction>[] => {
    return [
      {
        column: AntimalwareAntiExploitSystemWideDetectionsWindowsAction.REPORT_ONLY,
        label: translateFn('Report only'),
      },
      {
        column: AntimalwareAntiExploitSystemWideDetectionsWindowsAction.BLOCK_PROCESS,
        label: translateFn('Block only'),
      },
      {
        column: AntimalwareAntiExploitSystemWideDetectionsWindowsAction.BLOCK_AND_REPORT,
        label: translateFn('Block and report'),
      },
      {
        column: AntimalwareAntiExploitSystemWideDetectionsWindowsAction.KILL_PROCESS,
        label: translateFn('Kill process'),
      },
    ];
  };

export const systemWideDetectionsWindowsRows = (
  searchableKeywords: {
    [type in AntimalwareAntiExploitSearchableKeywords]: SearchableKeyword;
  },
  isFirstRowHidden: boolean
): RowDetail<
  AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey,
  AntimalwareAntiExploitSystemWideDetectionsWindowsAction
>[] => [
  {
    key: AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.DPI_DETECTIONS,
    label: searchableKeywords[AntimalwareAntiExploitSearchableKeywords.ProcessIntrospection].translatedLabel,
    availableColumns: [
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.REPORT_ONLY,
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.KILL_PROCESS,
    ],
    tooltip: translateFn(
      'Performs a deep analysis of a process state and investigate possible indicators of compromise.'
    ),
    hidden: isFirstRowHidden,
  },
  {
    key: AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.PRIVILEGE_ESCALATION,
    label: searchableKeywords[AntimalwareAntiExploitSearchableKeywords.PrivilegeEscalation].translatedLabel,
    availableColumns: [
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.REPORT_ONLY,
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.KILL_PROCESS,
    ],
    tooltip: translateFn('Prevents processes from gaining unauthorized privileges and access to resources.'),
  },
  {
    key: AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.LSASS_PROTECTION,
    label: searchableKeywords[AntimalwareAntiExploitSearchableKeywords.LsassProtection].translatedLabel,
    availableColumns: [
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.REPORT_ONLY,
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.BLOCK_PROCESS,
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.BLOCK_AND_REPORT,
    ],
    tooltip: translateFn(
      'Protects the LSASS process from leaking secrets such as password hashes and security settings.'
    ),
  },
];

export function convertSystemWideDetectionsWindowsDbValueToFormValue(
  systemWideDetections: AntimalwareAntiExploitSystemWideDetectionsWindows
): ComplexSelectionFormValue<
  AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey,
  AntimalwareAntiExploitSystemWideDetectionsWindowsAction
> {
  return {
    [AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.DPI_DETECTIONS]: {
      enabled: systemWideDetections.dpiDetectionsStatus,
      column: systemWideDetections.dpiDetections,
    },
    [AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.PRIVILEGE_ESCALATION]: {
      enabled: systemWideDetections.privilegeEscalationStatus,
      column: systemWideDetections.privilegeEscalation,
    },
    [AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.LSASS_PROTECTION]: {
      enabled: systemWideDetections.lsassMemoryAccessFromUnknownProcessStatus,
      column: systemWideDetections.lsassMemoryAccessFromUnknownProcess,
    },
  };
}

export function convertSystemWideDetectionsWindowsFormValueToDbValue(
  systemWideDetections: ComplexSelectionFormValue<
    AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey,
    AntimalwareAntiExploitSystemWideDetectionsWindowsAction
  >
): AntimalwareAntiExploitSystemWideDetectionsWindows {
  return {
    privilegeEscalationStatus:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.PRIVILEGE_ESCALATION]?.enabled ??
      false,
    privilegeEscalation:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.PRIVILEGE_ESCALATION]?.column ??
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.REPORT_ONLY,
    dpiDetectionsStatus:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.DPI_DETECTIONS]?.enabled ?? false,
    dpiDetections:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.DPI_DETECTIONS]?.column ??
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.REPORT_ONLY,
    lsassMemoryAccessFromUnknownProcessStatus:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.LSASS_PROTECTION]?.enabled ??
      false,
    lsassMemoryAccessFromUnknownProcess:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey.LSASS_PROTECTION]?.column ??
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.REPORT_ONLY,
  };
}
/* System wide detections - Windows end region */

/* System wide detections - Linux region */
export enum AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey {
  CREDENTIALS = 'credentials',
  PTRACE = 'ptrace',
  NAMESPACE = 'namespace',
  CORRUPTION = 'corruption',
  PERMISSIONS = 'permissions',
}

export const systemWideDetectionsLinuxColumns =
  (): ColumnDetail<AntimalwareAntiExploitSystemWideDetectionsLinuxAction>[] => {
    return [
      {
        column: AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
        label: translateFn('Report only'),
      },
      {
        column: AntimalwareAntiExploitSystemWideDetectionsLinuxAction.KILL_PROCESS,
        label: translateFn('Kill process'),
      },
    ];
  };

export const systemWideDetectionsLinuxRows = (searchableKeywords: {
  [type in AntimalwareAntiExploitSearchableKeywords]: SearchableKeyword;
}): RowDetail<
  AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey,
  AntimalwareAntiExploitSystemWideDetectionsWindowsAction
>[] => [
  {
    key: AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.CREDENTIALS,
    label: searchableKeywords[AntimalwareAntiExploitSearchableKeywords.CredentialsMonitoring].translatedLabel,
    tooltip: translateFn('Inspects process behavior to detect unexpected credential changes for a thread.'),
  },
  {
    key: AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.PTRACE,
    label: searchableKeywords[AntimalwareAntiExploitSearchableKeywords.PtraceMonitoring].translatedLabel,
    tooltip: translateFn(
      'Monitors the use of debugging mechanisms to detect attempts of using ptrace to control a privileged process.'
    ),
  },
  {
    key: AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.NAMESPACE,
    label: searchableKeywords[AntimalwareAntiExploitSearchableKeywords.NamespaceMonitoring].translatedLabel,
    tooltip: translateFn('Inspects process behavior to detect unexpected changes in the namespace of a thread.'),
  },
  {
    key: AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.CORRUPTION,
    label: searchableKeywords[AntimalwareAntiExploitSearchableKeywords.CorruptionMonitoring].translatedLabel,
    tooltip: translateFn('Monitors syscall activity to detect memory corruption attempts via crafted syscalls.'),
  },
  {
    key: AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.PERMISSIONS,
    label: searchableKeywords[AntimalwareAntiExploitSearchableKeywords.SuidMonitoring].translatedLabel,
    tooltip: translateFn('Reports all attempts to set an SUID flag to a file.'),
  },
];

export function convertSystemWideDetectionsLinuxDbValueToFormValue(
  systemWideDetections: AntimalwareAntiExploitSystemWideDetectionsLinux
): ComplexSelectionFormValue<
  AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey,
  AntimalwareAntiExploitSystemWideDetectionsLinuxAction
> {
  return {
    [AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.CREDENTIALS]: {
      enabled: systemWideDetections.credentialsStatus,
      column: systemWideDetections.credentials,
    },
    [AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.PTRACE]: {
      enabled: systemWideDetections.ptraceStatus,
      column: systemWideDetections.ptrace,
    },
    [AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.NAMESPACE]: {
      enabled: systemWideDetections.namespaceStatus,
      column: systemWideDetections.namespace,
    },
    [AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.CORRUPTION]: {
      enabled: systemWideDetections.corruptionStatus,
      column: systemWideDetections.corruption,
    },
    [AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.PERMISSIONS]: {
      enabled: systemWideDetections.permissionsStatus,
      column: systemWideDetections.permissions,
    },
  };
}

export function convertSystemWideDetectionsLinuxFormValueToDbValue(
  systemWideDetections: ComplexSelectionFormValue<
    AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey,
    AntimalwareAntiExploitSystemWideDetectionsLinuxAction
  >
): AntimalwareAntiExploitSystemWideDetectionsLinux {
  return {
    credentialsStatus:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.CREDENTIALS]?.enabled ?? false,
    credentials:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.CREDENTIALS]?.column ??
      AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
    ptraceStatus:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.PTRACE]?.enabled ?? false,
    ptrace:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.PTRACE]?.column ??
      AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
    namespaceStatus:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.NAMESPACE]?.enabled ?? false,
    namespace:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.NAMESPACE]?.column ??
      AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
    corruptionStatus:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.CORRUPTION]?.enabled ?? false,
    corruption:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.CORRUPTION]?.column ??
      AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
    permissionsStatus:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.PERMISSIONS]?.enabled ?? false,
    permissions:
      systemWideDetections[AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey.PERMISSIONS]?.column ??
      AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY,
  };
}
/* System wide detections - Linux end region */
